import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { StoreProvider, useStore } from "@/context/StoreContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, Store, Package, Receipt, LogOut, ExternalLink, Inbox,
  PanelTop, Settings, MessageSquare, Mail, BarChart3, Scale, ChevronsUpDown, Menu, X,
} from "lucide-react";

const links = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/stores", label: "Stores", icon: Store },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/orders", label: "Orders", icon: Receipt },
  { to: "/admin/leads", label: "Leads", icon: Inbox },
  { to: "/admin/frontpage", label: "Frontpage", icon: PanelTop },
  { to: "/admin/legal", label: "Legal", icon: Scale },
  { to: "/admin/popups", label: "Popup", icon: MessageSquare },
  { to: "/admin/mailer", label: "Mailer", icon: Mail },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/admin/settings", label: "Settings & Security", icon: Settings },
];

function StoreSwitcher() {
  const { stores, selectedStore, setSelectedStore } = useStore();
  return (
    <Select value={selectedStore} onValueChange={setSelectedStore}>
      <SelectTrigger
        className="h-9 w-[150px] sm:w-[190px] rounded-lg border-zinc-200 bg-white text-sm font-medium text-zinc-700 shadow-none focus:ring-2 focus:ring-brand/20"
        data-testid="store-switcher"
      >
        <ChevronsUpDown size={13} className="mr-1 text-zinc-400" strokeWidth={1.5} />
        <SelectValue placeholder="All stores" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All stores</SelectItem>
        {stores.map((s) => (
          <SelectItem key={s.id} value={s.id} data-testid={`switcher-option-${s.handle}`}>{s.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function LayoutInner() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const active = [...links].reverse().find((l) =>
    l.end ? location.pathname === l.to : location.pathname.startsWith(l.to)
  ) || links[0];

  const initials = (user?.name || user?.email || "?")
    .split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");

  return (
    <div className="admin-shell min-h-screen bg-[#fafafb] font-body text-zinc-800">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={() => setMobileOpen(false)} data-testid="sidebar-overlay" />
      )}

      <aside
        className={`w-[260px] bg-white border-r border-zinc-200 flex flex-col fixed inset-y-0 left-0 z-50 transition-transform duration-300 ease-out lg:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="admin-sidebar"
      >
        <div className="h-16 flex items-center justify-between px-6 border-b border-zinc-100">
          <span className="font-heading font-extrabold text-xl tracking-tight text-zinc-900">
            STORE<span className="text-brand">IG</span>
          </span>
          <button className="lg:hidden text-zinc-400 hover:text-zinc-900" onClick={() => setMobileOpen(false)} data-testid="sidebar-close">
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto no-scrollbar">
          {links.map((l, i) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              onClick={() => setMobileOpen(false)}
              style={{ animationDelay: `${i * 25}ms` }}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 pl-4 pr-3 py-2.5 rounded-lg border-l-2 text-sm transition-colors duration-200 ${
                  isActive
                    ? "border-brand bg-brand/[0.06] text-zinc-900 font-semibold"
                    : "border-transparent text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <l.icon size={18} strokeWidth={1.5} className={isActive ? "text-brand" : "text-zinc-400 group-hover:text-zinc-600"} />
                  {l.label}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-zinc-100">
          <a
            href="/" target="_blank" rel="noreferrer"
            className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50 transition-colors duration-200"
            data-testid="view-storefront-link"
          >
            <ExternalLink size={18} strokeWidth={1.5} className="text-zinc-400" /> View storefront
          </a>
        </div>
      </aside>

      <div className="lg:ml-[260px] flex flex-col min-h-screen">
        <header className="sticky top-0 z-30 h-16 backdrop-blur-xl bg-white/70 border-b border-zinc-200 flex items-center justify-between px-4 sm:px-6 lg:px-8" data-testid="admin-topbar">
          <div className="flex items-center gap-3 min-w-0">
            <button className="lg:hidden text-zinc-500 hover:text-zinc-900 shrink-0" onClick={() => setMobileOpen(true)} data-testid="sidebar-open">
              <Menu size={20} strokeWidth={1.5} />
            </button>
            <div className="flex items-center gap-2 text-sm min-w-0" data-testid="admin-breadcrumb">
              <span className="text-zinc-400 font-medium hidden sm:inline">StoreIG</span>
              <span className="text-zinc-300 hidden sm:inline">/</span>
              <span className="font-heading font-semibold text-zinc-900 tracking-tight truncate">{active.label}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <StoreSwitcher />
            <div className="h-6 w-px bg-zinc-200 hidden sm:block" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full p-1 hover:bg-zinc-100 transition-colors duration-200 outline-none focus-visible:ring-2 focus-visible:ring-brand/30" data-testid="admin-profile-trigger">
                  <Avatar className="h-8 w-8 border border-zinc-200">
                    <AvatarImage src={user?.picture} alt={user?.name} referrerPolicy="no-referrer" />
                    <AvatarFallback className="bg-brand/10 text-brand text-xs font-semibold">{initials}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <div className="text-sm font-semibold text-zinc-900 truncate">{user?.name}</div>
                  <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/admin/settings")} data-testid="menu-settings" className="cursor-pointer">
                  <Settings size={15} strokeWidth={1.5} className="mr-2 text-zinc-500" /> Settings & Security
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-button" className="cursor-pointer text-rose-600 focus:text-rose-600">
                  <LogOut size={15} strokeWidth={1.5} className="mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  return (
    <StoreProvider>
      <LayoutInner />
    </StoreProvider>
  );
}
